import type { NotifyPrefsDto } from '@werft/contracts';
import { api, newOpId } from './api.js';

/**
 * Web Push auf diesem Gerät (P23, ADR 0026). Anmelden braucht Netz (Push-Dienst des Browsers) und eine Erlaubnis
 * der Person – deshalb direkt statt über den Ausgangskorb; die opId entsteht trotzdem vor dem Senden.
 */
export type PushSupport = 'ok' | 'install' | 'unsupported';

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

export function pushSupport(): PushSupport {
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) return 'ok';
  // iPhone/iPad: Push nur in der installierten App (Zum Home-Bildschirm)
  if (isIos() && !standalone()) return 'install';
  return 'unsupported';
}

export const permission = (): NotificationPermission => ('Notification' in window ? Notification.permission : 'denied');

async function registration() {
  return (await navigator.serviceWorker.getRegistration()) ?? navigator.serviceWorker.register('/sw.js');
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'ok') return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(s.length));
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/** Kurzer Name des Geräts für die Liste („Chrome auf Android“) */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Android/.test(ua) ? 'Android' : isIos() ? 'iPhone/iPad' : /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} auf ${os}` : browser;
}

/** Erlaubnis holen, beim Push-Dienst anmelden, Gerät am Server eintragen */
export async function enablePush(vapidKey: string): Promise<NotifyPrefsDto> {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('denied');
  const reg = await registration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // Anderer Schlüssel (Server-Geheimnis gewechselt): neu anmelden
  if (sub && sub.options.applicationServerKey) {
    const have = new Uint8Array(sub.options.applicationServerKey);
    const want = keyBytes(vapidKey);
    if (have.length !== want.length || have.some((b, i) => b !== want[i])) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(vapidKey) });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  const opId = newOpId();
  return api.post<NotifyPrefsDto>('/api/me/push', { opId, endpoint: json.endpoint, keys: json.keys, label: deviceLabel() });
}

/** Dieses Gerät abmelden (auch beim Abmelden aus Werft: sonst bekäme das nächste Konto fremde Hinweise) */
export async function disablePush(): Promise<NotifyPrefsDto | null> {
  const sub = await currentSubscription().catch(() => null);
  if (!sub) return null;
  const opId = newOpId();
  const r = await api.post<NotifyPrefsDto>('/api/me/push/remove', { opId, endpoint: sub.endpoint }).catch(() => null);
  await sub.unsubscribe().catch(() => {});
  return r;
}
